// Puzzles modal — five difficulty tiers (Easy → Hyper-extreme),
// two puzzles per tier. Mirrors CoursesModal's structure: a
// category shelf, then a list of puzzles in the picked category,
// with a back arrow and an Esc-closes-backdrop affordance. Each
// puzzle card shows its quest + a Start button; the parent picks
// the puzzle up via `onPickPuzzle` and the runner stamps it.

import { useEffect, useState } from "react";

import { puzzlesByCategory } from "./data";
import type { Puzzle, PuzzleCategory } from "./types";
import { CATEGORIES, CATEGORY_META } from "./types";

export interface PuzzlesModalProps {
  open: boolean;
  onClose: () => void;
  onPickPuzzle: (puzzle: Puzzle) => void;
  /** Stable id set of completed puzzles — drives the ✓ chip. */
  completedIds: Set<string>;
}

export function PuzzlesModal({
  open,
  onClose,
  onPickPuzzle,
  completedIds,
}: PuzzlesModalProps) {
  const [openedCategory, setOpenedCategory] = useState<PuzzleCategory | null>(null);

  useEffect(() => {
    if (open) setOpenedCategory(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="puzzles-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="puzzles-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Puzzles"
      >
        <header className="puzzles-modal-header">
          <h2 className="puzzles-modal-title">
            {openedCategory === null
              ? "Puzzles"
              : `${CATEGORY_META[openedCategory].title} puzzles`}
          </h2>
          <div className="puzzles-modal-header-actions">
            {openedCategory !== null && (
              <button
                type="button"
                className="puzzles-link"
                onClick={() => setOpenedCategory(null)}
                aria-label="Back to categories"
              >
                ← Categories
              </button>
            )}
            <button
              type="button"
              className="puzzles-close"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
          </div>
        </header>

        {openedCategory === null ? (
          <CategoryShelf
            onPick={setOpenedCategory}
            completedIds={completedIds}
          />
        ) : (
          <PuzzleList
            category={openedCategory}
            completedIds={completedIds}
            onPick={(p) => {
              onPickPuzzle(p);
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}

function CategoryShelf(props: {
  onPick: (c: PuzzleCategory) => void;
  completedIds: Set<string>;
}) {
  return (
    <div className="puzzles-shelf">
      {CATEGORIES.map((cat) => {
        const meta = CATEGORY_META[cat];
        const all = puzzlesByCategory(cat);
        const done = all.filter((p) => props.completedIds.has(p.id)).length;
        return (
          <button
            key={cat}
            type="button"
            className="puzzles-shelf-card"
            onClick={() => props.onPick(cat)}
            style={{ borderColor: meta.tone }}
          >
            <span
              className="puzzles-shelf-dot"
              style={{ background: meta.tone }}
              aria-hidden
            />
            <span className="puzzles-shelf-title">{meta.title}</span>
            <span className="puzzles-shelf-subtitle">{meta.subtitle}</span>
            <span className="puzzles-shelf-progress">
              {done} / {all.length} complete
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PuzzleList(props: {
  category: PuzzleCategory;
  completedIds: Set<string>;
  onPick: (p: Puzzle) => void;
}) {
  const puzzles = puzzlesByCategory(props.category);
  return (
    <div className="puzzles-list">
      {puzzles.map((p) => {
        const done = props.completedIds.has(p.id);
        return (
          <article
            key={p.id}
            className={"puzzles-list-card" + (done ? " is-done" : "")}
          >
            <header className="puzzles-list-card-head">
              <h3 className="puzzles-list-card-title">{p.name}</h3>
              {done && (
                <span
                  className="puzzles-list-card-done"
                  aria-label="Completed"
                  title="You completed this puzzle"
                >
                  ✓
                </span>
              )}
            </header>
            <p className="puzzles-list-card-quest">{p.quest}</p>
            {p.hint && (
              <p className="puzzles-list-card-hint">Hint: {p.hint}</p>
            )}
            <button
              type="button"
              className="puzzles-list-card-start"
              onClick={() => props.onPick(p)}
            >
              {done ? "Retry" : "Start"}
            </button>
          </article>
        );
      })}
    </div>
  );
}
